/**
 * OrganizationDetails Component
 *
 * Displays detailed information about a partner organization.
 * Fetches organization details, stats, and recent orders from the API.
 *
 * Uses:
 * - useCustomerOrganization hook for data fetching (includes stats and recent orders)
 * - useAuthConfig hook for authentication
 */

import React from 'react';
import {
  ArrowLeft,
  Building2,
  ShieldCheck,
  Calendar,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  PlusCircle,
  Package,
  Loader2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import {
  useCustomerOrganization,
  type CustomerOrganization,
  type OrganizationStats,
} from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

interface OrganizationDetailsProps {
  orgId: string;
  onBack: () => void;
  onNewOrder: (id: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge for Orders
// ─────────────────────────────────────────────────────────────────────────────

type OrderStatus = 'Draft' | 'Placed' | 'In Transit' | 'Completed' | 'Cancelled';

const StatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const styles: Record<OrderStatus, string> = {
    Draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    Placed: 'bg-blue-50 text-blue-700 border-blue-200',
    'In Transit': 'bg-amber-50 text-amber-700 border-amber-200',
    Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <span
      className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${styles[status] || styles.Draft}`}
    >
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading State
// ─────────────────────────────────────────────────────────────────────────────

const LoadingState: React.FC = () => (
  <div
    data-testid="organization-details-loading"
    className="flex flex-col h-full bg-white text-zinc-900"
  >
    {/* Header Skeleton */}
    <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-zinc-200 rounded animate-pulse" />
        <div className="h-4 w-40 bg-zinc-200 rounded animate-pulse" />
      </div>
      <div className="h-8 w-28 bg-zinc-200 rounded animate-pulse" />
    </div>

    {/* Content Skeleton */}
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Panel Skeleton */}
          <div className="md:col-span-1 space-y-6">
            <div className="p-6 border border-zinc-200 rounded-xl bg-zinc-50/30 flex flex-col items-center">
              <div className="w-20 h-20 bg-zinc-200 rounded-2xl animate-pulse mb-4" />
              <div className="h-5 w-32 bg-zinc-200 rounded animate-pulse mb-2" />
              <div className="h-3 w-24 bg-zinc-200 rounded animate-pulse" />
              <div className="w-full h-px bg-zinc-200 my-6" />
              <div className="w-full space-y-4">
                <div className="h-4 w-full bg-zinc-200 rounded animate-pulse" />
                <div className="h-4 w-full bg-zinc-200 rounded animate-pulse" />
              </div>
            </div>
          </div>

          {/* Right Panel Skeleton */}
          <div className="md:col-span-2 space-y-8">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 border border-zinc-200 rounded-xl">
                <div className="h-3 w-24 bg-zinc-200 rounded animate-pulse mb-2" />
                <div className="h-8 w-16 bg-zinc-200 rounded animate-pulse" />
              </div>
              <div className="p-5 border border-zinc-200 rounded-xl">
                <div className="h-3 w-24 bg-zinc-200 rounded animate-pulse mb-2" />
                <div className="h-8 w-16 bg-zinc-200 rounded animate-pulse" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-3 w-20 bg-zinc-200 rounded animate-pulse" />
              <div className="p-6 border border-zinc-200 rounded-xl">
                <div className="h-4 w-full bg-zinc-200 rounded animate-pulse mb-2" />
                <div className="h-4 w-3/4 bg-zinc-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Error State
// ─────────────────────────────────────────────────────────────────────────────

const ErrorState: React.FC<{ onRetry: () => void; onBack: () => void }> = ({ onRetry, onBack }) => (
  <div className="flex flex-col items-center justify-center h-full bg-white p-8">
    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
      <XCircle className="w-8 h-8 text-red-500" />
    </div>
    <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to Load Organization</h3>
    <p className="text-sm text-zinc-500 mb-4 text-center max-w-sm">
      We couldn't load the organization details. Please check your connection and try again.
    </p>
    <div className="flex items-center gap-3">
      <button
        onClick={onBack}
        className="flex items-center gap-2 px-4 py-2 text-zinc-600 text-sm font-medium rounded-lg hover:bg-zinc-100 transition-colors border border-zinc-200"
      >
        <ArrowLeft size={16} />
        Go Back
      </button>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
      >
        <RefreshCw size={16} />
        Retry
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationDetails: React.FC<OrganizationDetailsProps> = ({
  orgId,
  onBack,
  onNewOrder,
}) => {
  const authConfig = useAuthConfig();

  // Fetch organization details with stats and recent orders from API
  const { data, isLoading, isError, refetch } = useCustomerOrganization(orgId, { authConfig });

  const organization = data?.organization;
  const stats = data?.stats;
  const recentOrders = data?.recent_orders ?? [];
  const isActive = organization?.status === 'active';

  // Show loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Show error state
  if (isError || !organization) {
    return <ErrorState onRetry={() => refetch()} onBack={onBack} />;
  }

  return (
    <div className="flex flex-col h-full bg-white text-zinc-900">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-zinc-500 hover:text-zinc-800 transition-colors p-1 hover:bg-zinc-100 rounded"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-zinc-400" />
            <h2 className="text-sm font-semibold text-zinc-900">{organization.name}</h2>
            <span
              className={`px-2 py-0.5 text-[10px] font-bold rounded-full border uppercase tracking-wide ${
                isActive
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                  : 'bg-red-50 text-red-600 border-red-200'
              }`}
            >
              {organization.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <button
              onClick={() => onNewOrder(organization.id)}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 text-white text-xs font-medium rounded-md hover:bg-zinc-800 transition-colors shadow-sm"
            >
              <PlusCircle size={14} />
              Dispatch Order
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="max-w-4xl mx-auto p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Panel: Profile Info */}
            <div className="md:col-span-1 space-y-6">
              <div className="p-6 border border-zinc-200 rounded-xl bg-zinc-50/30 flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-white border border-zinc-200 rounded-2xl flex items-center justify-center text-zinc-400 shadow-sm mb-4">
                  <Building2 size={40} />
                </div>
                <h1 className="text-lg font-bold text-zinc-900">{organization.name}</h1>
                <p className="text-xs text-zinc-500 mt-1">
                  ID: <span className="font-mono">{organization.id}</span>
                </p>

                <div className="w-full h-px bg-zinc-200 my-6"></div>

                <div className="w-full space-y-4">
                  <div className="flex items-center gap-3 text-xs text-zinc-600">
                    <Calendar size={14} className="text-zinc-400 shrink-0" />
                    <span>
                      Partner since {new Date(organization.joined_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-600">
                    <ShieldCheck
                      size={14}
                      className={isActive ? 'text-emerald-500 shrink-0' : 'text-zinc-300 shrink-0'}
                    />
                    <span>{isActive ? 'Active Partnership' : 'Suspended Partnership'}</span>
                  </div>
                </div>
              </div>

              <div className="p-6 border border-zinc-200 rounded-xl space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                  Contact Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-zinc-600">
                    <Mail size={16} className="text-zinc-400" />
                    <span className="truncate">
                      {organization.contact_email || 'support@cfleet.io'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-zinc-600">
                    <Phone size={16} className="text-zinc-400" />
                    <span>{organization.contact_phone || '+1 (555) 000-0000'}</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-zinc-600">
                    <MapPin size={16} className="text-zinc-400 mt-0.5" />
                    <span>
                      Headquarters Office,
                      <br />
                      Miami Logistics Hub
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Metrics & Activity */}
            <div className="md:col-span-2 space-y-8">
              {/* Stats Grid - Now from API */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 border border-zinc-200 rounded-xl bg-white shadow-sm">
                  <div className="text-xs font-medium text-zinc-500 mb-1">Total Dispatched</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-zinc-900">
                      {stats?.total_orders ?? 0}
                    </span>
                    <span className="text-[10px] text-zinc-400">orders</span>
                  </div>
                </div>
                <div className="p-5 border border-zinc-200 rounded-xl bg-white shadow-sm">
                  <div className="text-xs font-medium text-zinc-500 mb-1">Completion Rate</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-zinc-900">
                      {stats?.completion_rate != null ? Math.round(stats.completion_rate) : 0}%
                    </span>
                    <span className="text-[10px] text-emerald-500 font-medium">Verified</span>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 ml-1">
                  About Partner
                </h3>
                <div className="p-6 border border-zinc-200 rounded-xl text-sm text-zinc-600 leading-relaxed bg-zinc-50/20">
                  {organization.description ||
                    'This delivery partner provides specialized logistics services for your enterprise. They are currently maintaining high performance ratings and are authorized for all standard delivery routes within your region.'}
                </div>
              </div>

              {/* Recent Activity Section - Now from API */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 ml-1">
                  Recent Shipments
                </h3>
                <div className="border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100">
                  {recentOrders.length > 0 ? (
                    recentOrders.slice(0, 3).map((order) => {
                      const createdAt = order.created_at;
                      return (
                        <div
                          key={order.id}
                          className="p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Package size={16} className="text-zinc-400" />
                            <div>
                              <div className="text-sm font-medium text-zinc-900">
                                Order #{order.id}
                              </div>
                              <div className="text-[10px] text-zinc-500">
                                {createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <StatusBadge status={order.status as OrderStatus} />
                            <div className="text-sm font-mono text-zinc-600">
                              ${order.price.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-12 text-center text-zinc-400 text-sm italic">
                      No order history with this partner yet.
                    </div>
                  )}
                </div>
              </div>

              {/* Partner Actions */}
              {!isActive && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-red-900">Partnership Restricted</h4>
                    <p className="text-xs text-red-700 mt-1">
                      This partner account is currently disabled. You cannot dispatch new orders to
                      them until their status is restored by an administrator.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
